import React, { useEffect, useMemo, useState } from "react";
import { Wallet, ExternalLink, TrendingUp, Users, Clock, CheckCircle2, RefreshCw, ShieldCheck, Trash2, AlertTriangle, CalendarClock, Gift, X } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader, Button, Card, Badge, SearchInput, EmptyState } from "@/components/ui-em";

function fmtUSDT(v) {
  const n = parseFloat(v);
  if (isNaN(n)) return "0";
  return Number.isInteger(n) ? String(n) : n.toString().replace(/0+$/, "").replace(/\.$/, "");
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-GB", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function truncate(s, head = 10, tail = 6) {
  if (!s || s.length <= head + tail + 2) return s;
  return `${s.slice(0, head)}…${s.slice(-tail)}`;
}

function Stat({ icon: Icon, label, value, hint, accent = false }) {
  return (
    <div className="em-card p-5" data-testid={`payments-stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-start justify-between mb-4">
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${accent ? "bg-gold/15 border border-gold/30" : "bg-white/5 border border-white/10"}`}>
          <Icon className={`w-4 h-4 ${accent ? "text-gold" : "text-zinc-400"}`} strokeWidth={1.75} />
        </div>
      </div>
      <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold mb-1">{label}</div>
      <div className={`font-display text-3xl font-light tracking-tight ${accent ? "text-gold" : "text-white"}`}>{value}</div>
      {hint && <div className="text-[11px] text-zinc-600 mt-1">{hint}</div>}
    </div>
  );
}

export default function AdminPayments() {
  const [data, setData] = useState({ verified: [], pending_users: [], expiring_soon: [], expired: [] });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState({});
  const [grantOpen, setGrantOpen] = useState(false);
  const [grantEmail, setGrantEmail] = useState("");
  const [grantName, setGrantName] = useState("");
  const [grantNote, setGrantNote] = useState("");
  const [grantBusy, setGrantBusy] = useState(false);

  const load = async () => {
    setRefreshing(true);
    try {
      const { data } = await api.get("/admin/payments");
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const stats = useMemo(() => {
    const verified = data.verified || [];
    const onChain = verified.filter((p) => p.source !== "admin_override");
    const totalUsdt = onChain.reduce((sum, p) => sum + (parseFloat(p.amount_usdt) || 0), 0);
    const avg = onChain.length ? totalUsdt / onChain.length : 0;
    return {
      totalUsdt,
      count: verified.length,
      onChainCount: onChain.length,
      overrideCount: verified.length - onChain.length,
      avg,
      pending: (data.pending_users || []).length,
    };
  }, [data]);

  // Group verified payments by calendar month for the revenue breakdown.
  // Only on-chain payments count as revenue (manual overrides = 0 USDT).
  const monthly = useMemo(() => {
    const verified = data.verified || [];
    const buckets = new Map();
    for (const p of verified) {
      if (p.source === "admin_override" || !p.verified_at) continue;
      const d = new Date(p.verified_at);
      if (isNaN(d.getTime())) continue;
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      const label = d.toLocaleString("en-GB", { month: "long", year: "numeric" });
      const cur = buckets.get(key) || { key, label, revenue: 0, count: 0 };
      cur.revenue += parseFloat(p.amount_usdt) || 0;
      cur.count += 1;
      buckets.set(key, cur);
    }
    const rows = Array.from(buckets.values()).sort((a, b) => b.key.localeCompare(a.key));
    const max = rows.reduce((m, r) => Math.max(m, r.revenue), 0);
    return { rows, max };
  }, [data]);

  const markPaid = async (userId, email) => {
    if (!window.confirm(`Mark ${email} as paid without on-chain payment?`)) return;
    setBusy((b) => ({ ...b, [userId]: "mark" }));
    try {
      await api.post(`/admin/payments/${userId}/mark-paid`, { note: "Admin manual override from dashboard" });
      toast.success("Marked as paid");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy((b) => ({ ...b, [userId]: null }));
    }
  };

  const exempt = async (userId, email) => {
    if (!window.confirm(`Exempt ${email} from payment (comp account)?`)) return;
    setBusy((b) => ({ ...b, [userId]: "exempt" }));
    try {
      await api.post(`/admin/users/${userId}/exempt-payment`, {});
      toast.success("Exempted");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy((b) => ({ ...b, [userId]: null }));
    }
  };

  const deletePayment = async (paymentId, email) => {
    if (!window.confirm(`Delete this payment record for ${email}? This is meant for cleaning up stale/manual records.`)) return;
    setBusy((b) => ({ ...b, [paymentId]: "delete" }));
    try {
      await api.delete(`/admin/payments/${paymentId}`);
      toast.success("Payment record removed");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy((b) => ({ ...b, [paymentId]: null }));
    }
  };

  const submitGrant = async (e) => {
    e.preventDefault();
    if (grantBusy) return;
    const email = grantEmail.trim().toLowerCase();
    if (!email || !/^\S+@\S+\.\S+$/.test(email)) {
      toast.error("Please enter a valid email");
      return;
    }
    setGrantBusy(true);
    try {
      const { data: res } = await api.post("/admin/users/grant-free-access", {
        email,
        name: grantName.trim() || undefined,
        note: grantNote.trim() || undefined,
      });
      toast.success(
        res.created
          ? `Free access granted to ${res.email} — invitation email sent.`
          : `${res.email} now has free access — notification email sent.`
      );
      setGrantEmail("");
      setGrantName("");
      setGrantNote("");
      setGrantOpen(false);
      await load();
    } catch (e2) {
      toast.error(formatApiError(e2));
    } finally {
      setGrantBusy(false);
    }
  };

  const filteredVerified = (data.verified || []).filter((p) =>
    !q ? true : (p.email || "").toLowerCase().includes(q.toLowerCase()) || (p.tx_hash || "").toLowerCase().includes(q.toLowerCase())
  );

  return (
    <div className="space-y-8">
      <PageHeader
        eyebrow="Admin · Revenue"
        title="Payments"
        description="Live overview of activations, on-chain revenue and pending signups."
        actions={
          <div className="flex items-center gap-2">
            <Button icon={Gift} onClick={() => setGrantOpen(true)} data-testid="grant-free-access-btn">
              Grant free access
            </Button>
            <Button icon={RefreshCw} onClick={load} disabled={refreshing} data-testid="payments-refresh">
              {refreshing ? "Refreshing…" : "Refresh"}
            </Button>
          </div>
        }
      />

      {loading ? (
        <div className="em-card p-10 text-center text-sm text-zinc-500">Loading payments…</div>
      ) : (
        <>
          {/* Stats grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Stat
              icon={TrendingUp}
              label="Total revenue"
              value={`${fmtUSDT(stats.totalUsdt.toFixed(6))} USDT`}
              hint={`${stats.onChainCount} on-chain payment${stats.onChainCount === 1 ? "" : "s"}`}
              accent
            />
            <Stat
              icon={CheckCircle2}
              label="Activations"
              value={stats.count}
              hint={stats.overrideCount ? `${stats.overrideCount} manual override${stats.overrideCount === 1 ? "" : "s"}` : "All on-chain"}
            />
            <Stat
              icon={Clock}
              label="Pending payment"
              value={stats.pending}
              hint={stats.pending ? "Signups waiting to pay" : "No pending signups"}
            />
            <Stat
              icon={Wallet}
              label="Avg. payment"
              value={`${fmtUSDT(stats.avg.toFixed(6))} USDT`}
              hint="Across on-chain only"
            />
          </div>

          {/* Monthly revenue breakdown */}
          <div>
            <div className="flex items-baseline justify-between mb-4">
              <h2 className="font-display text-xl font-light tracking-tight text-white">Monthly revenue</h2>
              <span className="text-xs text-zinc-500">{monthly.rows.length} month{monthly.rows.length === 1 ? "" : "s"} tracked</span>
            </div>
            <Card className="p-6">
              {monthly.rows.length === 0 ? (
                <div className="text-center py-8">
                  <div className="text-sm text-zinc-500">No on-chain revenue yet — monthly breakdown will appear once payments come in.</div>
                </div>
              ) : (
                <div className="space-y-3" data-testid="payments-monthly-breakdown">
                  {monthly.rows.map((row) => {
                    const pct = monthly.max > 0 ? (row.revenue / monthly.max) * 100 : 0;
                    return (
                      <div key={row.key} className="grid grid-cols-[1fr_auto] items-center gap-4">
                        <div>
                          <div className="flex items-baseline justify-between mb-1.5">
                            <span className="text-sm text-white">{row.label}</span>
                            <span className="text-[11px] text-zinc-500">{row.count} payment{row.count === 1 ? "" : "s"}</span>
                          </div>
                          <div className="h-1.5 rounded-full bg-white/5 overflow-hidden">
                            <div
                              className="h-full bg-gold rounded-full"
                              style={{ width: `${pct}%`, transitionProperty: "width", transitionDuration: "600ms" }}
                            />
                          </div>
                        </div>
                        <div className="font-mono text-sm text-gold font-semibold min-w-[110px] text-right">
                          {fmtUSDT(row.revenue.toFixed(6))} USDT
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </Card>
          </div>

          {/* Renewals — expiring soon */}
          {(data.expiring_soon || []).length > 0 && (
            <div>
              <div className="flex items-baseline justify-between mb-4">
                <h2 className="font-display text-xl font-light tracking-tight text-white">Expiring soon</h2>
                <span className="text-xs text-zinc-500">{data.expiring_soon.length} leader{data.expiring_soon.length === 1 ? "" : "s"} within 30 days</span>
              </div>
              <Card className="overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="payments-expiring-table">
                    <thead>
                      <tr className="border-b border-white/5 text-[10px] uppercase tracking-wider text-zinc-500">
                        <th className="text-left font-semibold px-4 py-3">Name</th>
                        <th className="text-left font-semibold px-4 py-3">Email</th>
                        <th className="text-left font-semibold px-4 py-3">Expires</th>
                        <th className="text-right font-semibold px-4 py-3">Days left</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.expiring_soon.map((u) => (
                        <tr key={u.id} className="border-b border-white/[0.03] hover:bg-white/[0.02]" style={{ transitionProperty: "background-color", transitionDuration: "160ms" }}>
                          <td className="px-4 py-3 text-white">{u.name}</td>
                          <td className="px-4 py-3 text-zinc-400">{u.email}</td>
                          <td className="px-4 py-3 text-xs text-zinc-400">{fmtDate(u.paid_until)}</td>
                          <td className="px-4 py-3 text-right">
                            <span className={`inline-flex items-center gap-1.5 text-xs font-mono ${u.days_left <= 7 ? "text-amber-400" : "text-zinc-300"}`}>
                              <CalendarClock className="w-3.5 h-3.5" />
                              {u.days_left}d
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            </div>
          )}

          {/* Renewals — expired */}
          {(data.expired || []).length > 0 && (
            <div>
              <div className="flex items-baseline justify-between mb-4">
                <h2 className="font-display text-xl font-light tracking-tight text-white">Expired · needs renewal</h2>
                <span className="text-xs text-zinc-500">{data.expired.length} leader{data.expired.length === 1 ? "" : "s"} locked out</span>
              </div>
              <Card className="overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="payments-expired-table">
                    <thead>
                      <tr className="border-b border-white/5 text-[10px] uppercase tracking-wider text-zinc-500">
                        <th className="text-left font-semibold px-4 py-3">Name</th>
                        <th className="text-left font-semibold px-4 py-3">Email</th>
                        <th className="text-left font-semibold px-4 py-3">Expired on</th>
                        <th className="text-right font-semibold px-4 py-3">Overdue</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.expired.map((u) => (
                        <tr key={u.id} className="border-b border-white/[0.03] hover:bg-white/[0.02]" style={{ transitionProperty: "background-color", transitionDuration: "160ms" }}>
                          <td className="px-4 py-3 text-white">{u.name}</td>
                          <td className="px-4 py-3 text-zinc-400">{u.email}</td>
                          <td className="px-4 py-3 text-xs text-zinc-400">{fmtDate(u.paid_until)}</td>
                          <td className="px-4 py-3 text-right">
                            <span className="inline-flex items-center gap-1.5 text-xs font-mono text-red-400">
                              <AlertTriangle className="w-3.5 h-3.5" />
                              {Math.abs(u.days_left)}d
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            </div>
          )}

          {/* Verified payments */}
          <div>
            <div className="flex items-baseline justify-between mb-4">
              <h2 className="font-display text-xl font-light tracking-tight text-white">Verified payments</h2>
              <span className="text-xs text-zinc-500">{filteredVerified.length} shown</span>
            </div>
            <SearchInput value={q} onChange={setQ} placeholder="Search by email or tx hash..." />
            <Card className="overflow-hidden mt-4">
              {filteredVerified.length === 0 ? (
                <EmptyState
                  icon={Wallet}
                  title="No payments yet"
                  description="Once affiliates sign up and complete their 150 USDT payment, they'll appear here in real-time."
                  className="m-4"
                />
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="payments-verified-table">
                    <thead>
                      <tr className="border-b border-white/5 text-[10px] uppercase tracking-wider text-zinc-500">
                        <th className="text-left font-semibold px-4 py-3">Email</th>
                        <th className="text-left font-semibold px-4 py-3">Amount</th>
                        <th className="text-left font-semibold px-4 py-3">Network</th>
                        <th className="text-left font-semibold px-4 py-3">TX / Source</th>
                        <th className="text-left font-semibold px-4 py-3">Verified at</th>
                        <th className="text-right font-semibold px-4 py-3 w-10"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredVerified.map((p) => {
                        const isOverride = p.source === "admin_override";
                        const etherscan = p.tx_hash && !p.tx_hash.startsWith("manual:")
                          ? `https://etherscan.io/tx/${p.tx_hash.startsWith("0x") ? p.tx_hash : "0x" + p.tx_hash}`
                          : null;
                        return (
                          <tr key={p.id} className="border-b border-white/[0.03] hover:bg-white/[0.02]" style={{ transitionProperty: "background-color", transitionDuration: "160ms" }}>
                            <td className="px-4 py-3 text-white">{p.email}</td>
                            <td className="px-4 py-3">
                              <span className={`font-semibold ${isOverride ? "text-zinc-500" : "text-gold"}`}>
                                {fmtUSDT(p.amount_usdt)} USDT
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              {isOverride ? (
                                <Badge>Manual</Badge>
                              ) : (
                                <span className="text-zinc-400">{p.network || "ERC-20"}</span>
                              )}
                            </td>
                            <td className="px-4 py-3">
                              {etherscan ? (
                                <a href={etherscan} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-xs text-zinc-400 hover:text-gold font-mono">
                                  {truncate(p.tx_hash)}
                                  <ExternalLink className="w-3 h-3" />
                                </a>
                              ) : (
                                <span className="text-xs text-zinc-500">{p.source === "admin_override" ? "Admin override" : "—"}</span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-xs text-zinc-500">{fmtDate(p.verified_at)}</td>
                            <td className="px-2 py-3 text-right">
                              <button
                                onClick={() => deletePayment(p.id, p.email)}
                                disabled={!!busy[p.id]}
                                data-testid={`payment-delete-${p.id}`}
                                title="Delete this payment record"
                                className="p-1.5 rounded-md text-zinc-600 hover:text-red-400 hover:bg-red-900/20 disabled:opacity-40"
                                style={{ transitionProperty: "color, background-color", transitionDuration: "160ms" }}
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
          </div>

          {/* Pending signups */}
          {(data.pending_users || []).length > 0 && (
            <div>
              <div className="flex items-baseline justify-between mb-4">
                <h2 className="font-display text-xl font-light tracking-tight text-white">Pending signups</h2>
                <span className="text-xs text-zinc-500">{(data.pending_users || []).length} waiting to pay</span>
              </div>
              <Card className="overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="payments-pending-table">
                    <thead>
                      <tr className="border-b border-white/5 text-[10px] uppercase tracking-wider text-zinc-500">
                        <th className="text-left font-semibold px-4 py-3">Name</th>
                        <th className="text-left font-semibold px-4 py-3">Email</th>
                        <th className="text-left font-semibold px-4 py-3">Role</th>
                        <th className="text-left font-semibold px-4 py-3">Invited</th>
                        <th className="text-right font-semibold px-4 py-3">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.pending_users.map((u) => (
                        <tr key={u.id} className="border-b border-white/[0.03] hover:bg-white/[0.02]" style={{ transitionProperty: "background-color", transitionDuration: "160ms" }}>
                          <td className="px-4 py-3 text-white">{u.name}</td>
                          <td className="px-4 py-3 text-zinc-400">{u.email}</td>
                          <td className="px-4 py-3"><Badge>{u.role}</Badge></td>
                          <td className="px-4 py-3 text-xs text-zinc-500">{fmtDate(u.invited_at)}</td>
                          <td className="px-4 py-3">
                            <div className="flex items-center justify-end gap-2">
                              <button
                                onClick={() => markPaid(u.id, u.email)}
                                disabled={!!busy[u.id]}
                                data-testid={`pending-mark-paid-${u.id}`}
                                className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-gold/10 border border-gold/30 text-gold hover:bg-gold/20 disabled:opacity-50"
                                style={{ transitionProperty: "background-color", transitionDuration: "160ms" }}
                              >
                                <ShieldCheck className="w-3.5 h-3.5" />
                                {busy[u.id] === "mark" ? "Working…" : "Mark paid"}
                              </button>
                              <button
                                onClick={() => exempt(u.id, u.email)}
                                disabled={!!busy[u.id]}
                                data-testid={`pending-exempt-${u.id}`}
                                className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg text-zinc-400 hover:text-white hover:bg-white/5 disabled:opacity-50"
                                style={{ transitionProperty: "color, background-color", transitionDuration: "160ms" }}
                              >
                                {busy[u.id] === "exempt" ? "Working…" : "Exempt"}
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            </div>
          )}
        </>
      )}

      {/* Grant free access modal */}
      {grantOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => !grantBusy && setGrantOpen(false)}
          data-testid="grant-modal-overlay"
        >
          <div
            className="em-card w-full max-w-md p-6 relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => !grantBusy && setGrantOpen(false)}
              className="absolute top-4 right-4 text-zinc-500 hover:text-white"
              aria-label="Close"
              data-testid="grant-close-btn"
            >
              <X className="w-4 h-4" />
            </button>
            <div className="flex items-center gap-3 mb-1">
              <div className="w-9 h-9 rounded-lg bg-gold/15 border border-gold/30 flex items-center justify-center">
                <Gift className="w-4 h-4 text-gold" strokeWidth={1.75} />
              </div>
              <p className="text-[10px] uppercase tracking-[0.2em] text-gold font-semibold">Comp account</p>
            </div>
            <h2 className="font-display text-2xl font-light tracking-tight mb-2">Grant free access</h2>
            <p className="text-xs text-zinc-500 leading-relaxed mb-5">
              Skip the 150 USDT paywall and give a leader lifetime access. If the email is already registered they're switched to <span className="text-zinc-300">exempt</span> and unlocked; new emails get an invitation to set a password.
            </p>
            <form onSubmit={submitGrant} className="space-y-4" data-testid="grant-form">
              <div>
                <label className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold mb-2 block">Email <span className="text-red-400">*</span></label>
                <input
                  data-testid="grant-email"
                  type="email"
                  value={grantEmail}
                  onChange={(e) => setGrantEmail(e.target.value)}
                  placeholder="leader@example.com"
                  required
                  autoFocus
                  className="em-input"
                />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold mb-2 block">Full name <span className="text-zinc-600">(optional — used only for new accounts)</span></label>
                <input
                  data-testid="grant-name"
                  value={grantName}
                  onChange={(e) => setGrantName(e.target.value)}
                  placeholder="Jane Doe"
                  className="em-input"
                />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold mb-2 block">Private note <span className="text-zinc-600">(optional — included in the welcome email)</span></label>
                <textarea
                  data-testid="grant-note"
                  value={grantNote}
                  onChange={(e) => setGrantNote(e.target.value)}
                  placeholder="Thanks for reporting the bug — free access on us."
                  rows={2}
                  className="em-input resize-none"
                />
              </div>
              <div className="flex items-center gap-3 pt-2">
                <button
                  type="submit"
                  disabled={grantBusy || !grantEmail}
                  data-testid="grant-submit"
                  className="flex-1 bg-gold text-ink-0 font-semibold rounded-lg px-4 py-2.5 hover:bg-gold-hover disabled:opacity-50 flex items-center justify-center gap-2"
                  style={{ transitionProperty: "background-color", transitionDuration: "180ms" }}
                >
                  <Gift className="w-4 h-4" />
                  {grantBusy ? "Granting…" : "Grant free access"}
                </button>
                <button
                  type="button"
                  onClick={() => setGrantOpen(false)}
                  disabled={grantBusy}
                  className="text-xs text-zinc-500 hover:text-white px-4 py-2.5"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
